<template>
  <div class="message-thread">
    <!-- Thread Header -->
    <div class="thread-header">
      <div class="header-info">
        <h3 class="conversation-title">{{ conversationTitle }}</h3>
        <span v-if="participantCount > 0" class="participant-count">
          {{ participantCount }} participant{{ participantCount !== 1 ? 's' : '' }}
        </span>
      </div>
      <div class="header-actions">
        <button
          v-if="isGroupConversation"
          class="btn btn-sm btn-outline-light"
          @click="$emit('manage-participants')"
          title="Manage participants"
        >
          <i class="fa-solid fa-users"></i>
        </button>
        <button
          v-if="isGroupConversation"
          class="btn btn-sm btn-outline-light"
          @click="$emit('leave-conversation')"
          title="Leave conversation"
        >
          <i class="fa-solid fa-right-from-bracket"></i>
        </button>
      </div>
    </div>

    <!-- Messages List -->
    <div
      ref="messagesContainer"
      class="messages-container"
      @scroll="handleScroll"
    >
      <div v-if="loading" class="loading-indicator">
        <i class="fa-solid fa-spinner fa-spin"></i>
        Loading messages...
      </div>

      <div
        v-if="hasMoreMessages && !loading"
        class="load-more"
        @click="$emit('load-more')"
      >
        <button class="btn btn-sm btn-link">Load earlier messages</button>
      </div>

      <div class="messages-list">
        <template v-for="(message, index) in messages">
          <!-- Unread divider appears before first unread message -->
          <div v-if="index === firstUnreadIndex" :key="'unread-' + message.id" class="unread-divider">
            <div class="unread-divider-content">New Messages</div>
          </div>
          <MessageItem :key="message.id" :message="message" @edit="handleEditMessage" />
        </template>
      </div>

      <div v-if="messages.length === 0 && !loading" class="no-messages">
        <i class="fa-regular fa-comments"></i>
        <p>No messages yet. Start the conversation!</p>
      </div>
    </div>

    <!-- Message Input -->
    <div class="message-input-container">
      <input
        ref="fileInput"
        type="file"
        multiple
        style="display: none"
        @change="onFilesSelected"
      >
      <button
        class="attach-button"
        title="Attach files"
        @click="$refs.fileInput.click()"
      >
        <i class="fa-solid fa-paperclip"></i>
      </button>
      <div class="input-area">
        <div v-if="pendingFiles.length > 0" class="pending-files">
          <div
            v-for="(file, index) in pendingFiles"
            :key="index"
            class="file-chip"
          >
            <span class="file-chip-name">{{ file.name }}</span>
            <button class="file-chip-remove" @click="removeFile(index)" title="Remove">×</button>
          </div>
        </div>
        <textarea
          ref="messageInput"
          v-model="newMessage"
          class="message-input"
          placeholder="Type a message..."
          rows="1"
          @input="autoResize"
          @keydown.enter.exact.prevent="sendMessage"
          @keydown.shift.enter.exact="newLine"
        ></textarea>
      </div>
      <button
        class="send-button"
        :disabled="(!newMessage.trim() && pendingFiles.length === 0) || sending"
        @click="sendMessage"
      >
        <i v-if="sending" class="fa-solid fa-spinner fa-spin"></i>
        <i v-else class="fa-solid fa-paper-plane"></i>
      </button>
    </div>
  </div>
</template>

<script>
import MessageItem from "./MessageItem.vue";
import { useMessagesStore } from "@/stores/messagesStore";

export default {
  name: "MessageThread",
  components: {
    MessageItem,
  },
  props: {
    conversation: {
      type: Object,
      default: null,
    },
    messages: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasMoreMessages: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      newMessage: "",
      sending: false,
      pendingFiles: [],
      messagesStore: useMessagesStore(),
    };
  },
  computed: {
    conversationTitle() {
      return this.messagesStore.getConversationDisplayName(this.conversation);
    },

    participantCount() {
      return this.conversation?.participants?.length || 0;
    },

    isGroupConversation() {
      return this.conversation?.type === "group";
    },

    firstUnreadIndex() {
      const pos = this.messagesStore.unreadDividerPosition;
      // Only show divider if it's for the current conversation
      if (!pos || pos.conversationId !== this.conversation?.id) return -1;
      return this.messages.findIndex(m => m.id === pos.messageId);
    },
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.scrollToBottom();
  },
  methods: {
    async sendMessage() {
      const hasContent = this.newMessage.trim();
      const hasFiles = this.pendingFiles.length > 0;
      if ((!hasContent && !hasFiles) || this.sending) return;

      this.sending = true;
      const content = this.newMessage.trim();
      const files = [...this.pendingFiles];
      this.newMessage = "";
      this.pendingFiles = [];

      try {
        await this.messagesStore.sendMessage(content, files);
        this.$nextTick(() => {
          this.scrollToBottom();
          this.resetTextareaHeight();
        });
      } catch (error) {
        this.$notifyError("Failed to send message");
        this.newMessage = content;
        this.pendingFiles = files;
      } finally {
        this.sending = false;
      }
    },

    onFilesSelected(e) {
      const selected = Array.from(e.target.files);
      this.pendingFiles = [...this.pendingFiles, ...selected];
      // Reset so the same file can be re-selected
      e.target.value = "";
    },

    removeFile(index) {
      this.pendingFiles.splice(index, 1);
    },

    async handleEditMessage({ messageId, content }) {
      try {
        await this.messagesStore.editMessage(messageId, content);
        this.$notifySuccess("Message updated");
      } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to edit message";
        this.$notifyError(errorMessage);
      }
    },

    handleScroll(event) {
      const { scrollTop } = event.target;
      if (scrollTop === 0 && this.hasMoreMessages && !this.loading) {
        this.$emit("load-more");
      }
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    autoResize() {
      const textarea = this.$refs.messageInput;
      if (textarea) {
        textarea.style.height = "auto";
        const newHeight = Math.min(textarea.scrollHeight, 120);
        textarea.style.height = newHeight + "px";
      }
    },

    resetTextareaHeight() {
      const textarea = this.$refs.messageInput;
      if (textarea) {
        textarea.style.height = "auto";
      }
    },

    newLine() {
      this.newMessage += "\n";
      this.$nextTick(() => {
        this.autoResize();
      });
    },
  },
};
</script>

<style scoped>
.message-thread {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
}

.thread-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px 8px 0 0;
}

.header-info {
  display: flex;
  flex-direction: column;
}

.conversation-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.participant-count {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.header-actions .btn {
  padding: 6px 10px;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.loading-indicator {
  text-align: center;
  padding: 20px;
  color: rgba(255, 255, 255, 0.6);
}

.load-more {
  text-align: center;
  margin-bottom: 16px;
}

.load-more .btn-link {
  color: #6BA6CD;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.no-messages {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
}

.no-messages i {
  font-size: 48px;
  margin-bottom: 16px;
}

.no-messages p {
  font-size: 14px;
  margin: 0;
}

.message-input-container {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0 0 8px 8px;
}

.attach-button {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
}

.attach-button:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.input-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pending-files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.file-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(107, 166, 205, 0.25);
  border: 1px solid rgba(107, 166, 205, 0.4);
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 12px;
  color: #fff;
  max-width: 200px;
}

.file-chip-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-chip-remove {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
  flex-shrink: 0;
}

.file-chip-remove:hover {
  color: #fff;
}

.message-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 14px;
  line-height: 1.4;
  resize: none;
  max-height: 120px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.message-input:focus {
  outline: none;
  border-color: #6BA6CD;
  box-shadow: 0 0 0 2px rgba(107, 166, 205, 0.2);
}

.message-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.send-button {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #6BA6CD, #4a8ab8);
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, opacity 0.2s;
}

.send-button:hover:not(:disabled) {
  transform: scale(1.05);
}

.send-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Scrollbar styling */
.messages-container::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track {
  background: transparent;
}

.messages-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.unread-divider {
  align-self: center;
  width: 100%;
  margin: 16px 0;
  padding-top: 12px;
  border-top: 1px solid #6BA6CD;
}

.unread-divider-content {
  text-align: center;
  font-size: 12px;
  color: #6BA6CD;
}
</style>
